/* oxlint-disable tailwindcss/no-unknown-classes -- Semantic duration tokens are not resolved from the CSS theme. */
import { motion } from "motion/react";
import type React from "react";
import { useState } from "react";
import { twJoin } from "tailwind-merge";

import type { MessageReactionDto, MessageV2Dto } from "@/api/types";
import imgLaugh from "@/assets/images/reactions/laugh.png";
import imgLike from "@/assets/images/reactions/like.png";
import imgLove from "@/assets/images/reactions/love.png";
import imgSad from "@/assets/images/reactions/sad.png";
import imgWow from "@/assets/images/reactions/wow.png";
import { ThumbsUpIcon } from "@/components/Icons/ThumbsUpIcon";
import { buttonVariants } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { getReactionImage, getReactionLabel } from "@/helpers/reactions";
import { twResolve } from "@/helpers/tw-resolve";
import { useBool } from "@/hooks/useBool";
import { useTranslation } from "@/translations";
import { Popover as ArkPopover } from "@/ui-adapters/ArkPopover";

type CommunityMessageReactionButtonProps = {
  size?: "sm" | "md";
  reaction: MessageV2Dto["requesterReactionType"];
  isDisabled?: boolean;
  onClickReaction: (reaction: MessageV2Dto["requesterReactionType"]) => void;
};
export function CommunityMessageReactionButton({
  size = "md",
  reaction,
  isDisabled,
  onClickReaction,
}: CommunityMessageReactionButtonProps): React.ReactNode {
  const [isMenuOpened, menuOpenHandlers] = useBool(false);
  const { t } = useTranslation();
  const [timer, setTimer] = useState<ReturnType<typeof setTimeout> | undefined>(undefined);

  const handleHover = () => {
    if (timer) {
      clearTimeout(timer);
      setTimer(undefined);
    }
    menuOpenHandlers.setTrue();
  };

  const handleUnhover = () => {
    const newTimer = setTimeout(menuOpenHandlers.setFalse, 500);
    setTimer(newTimer);
  };

  const handleClickReaction = (newReaction: MessageReactionDto["reactionType"] | undefined) => {
    if (newReaction === reaction) {
      onClickReaction(undefined);
    } else {
      onClickReaction(newReaction);
    }
    menuOpenHandlers.setFalse();
  };

  const availableReactions: {
    img: string;
    label: string;
    value: MessageReactionDto["reactionType"];
  }[] = [
    {
      img: imgLike,
      label: t("component.community-post.reaction.like.button"),
      value: "like",
    },
    {
      img: imgLove,
      label: t("component.community-post.reaction.love.button"),
      value: "love",
    },
    {
      img: imgLaugh,
      label: t("component.community-post.reaction.laugh.button"),
      value: "laugh",
    },
    {
      img: imgSad,
      label: t("component.community-post.reaction.sad.button"),
      value: "sad",
    },
    {
      img: imgWow,
      label: t("component.community-post.reaction.wow.button"),
      value: "wow",
    },
  ];

  return (
    <div
      className="relative z-20 flex"
      onMouseEnter={isDisabled ? undefined : handleHover}
      onMouseLeave={isDisabled ? undefined : handleUnhover}
    >
      <button
        type="button"
        data-testid={size === "md" ? "react-btn" : "comment-react-btn"}
        className={twResolve(
          buttonVariants({
            variant: size === "md" ? "subtle" : "ghostSecondary",
            size: size === "md" ? "md" : "sm",
            type: "button",
          }),
          size === "md" && "rounded-full",
          size === "md" && "text-text-brand hover:bg-background-brand-light focus-visible:bg-background-brand-light",
          size === "md" && !reaction && "font-normal",
          size === "md" && reaction && "text-body-emphasis",
          size === "sm" && reaction && "text-caption-emphasis",
          isMenuOpened && size === "md" && "bg-background-brand-light",
          isMenuOpened && size === "sm" && "text-text-brand",
          reaction && "text-text-brand",
        )}
        aria-label={reaction ? getReactionLabel(reaction, t) : undefined}
        onFocus={handleHover}
        onClick={() => handleClickReaction(reaction ?? "like")}
        disabled={isDisabled}
      >
        {reaction ? null : <ThumbsUpIcon />}
        <Inline as="span" gap="xs" align="center" wrap={false}>
          {reaction && (
            <img
              src={getReactionImage(reaction)}
              alt={getReactionLabel(reaction, t)}
              className={twJoin(size === "md" && "h-icon-control", size === "sm" && "h-icon-inline")}
            />
          )}
          <span>{reaction ? getReactionLabel(reaction, t) : t("component.community-post.like")}</span>
        </Inline>
      </button>

      {/* Invisible connector element that creates a hover bridge */}
      {isMenuOpened && (
        <>
          <div className="absolute -top-space-sm left-0 h-space-sm w-full" aria-hidden />
          <div className="absolute -bottom-space-sm left-0 h-space-sm w-full" aria-hidden />
        </>
      )}

      <ArkPopover.Root
        positioning={{
          placement: "top-start",
        }}
        open={isMenuOpened}
        onOpenChange={(details) => menuOpenHandlers.set(details.open)}
        autoFocus={false}
        closeOnEscape
        closeOnInteractOutside
        portalled
        lazyMount
        unmountOnExit
      >
        <ArkPopover.Anchor className="absolute bottom-0 left-0 h-full" />
        <ArkPopover.Positioner>
          <ArkPopover.Content>
            <motion.ul
              data-testid="reaction-menu"
              variants={{
                initial: {
                  scale: 0,
                },
                animate: {
                  scale: 1,
                },
              }}
              transition={{
                staggerChildren: 0.04,
              }}
              initial="initial"
              animate={isMenuOpened ? "animate" : "initial"}
              className="flex origin-bottom-left gap-space-sm overflow-x-hidden rounded-full border border-border-default bg-background-default p-space-sm shadow-overlay"
            >
              {availableReactions.map((availableReaction) => (
                <li key={availableReaction.value}>
                  <motion.button
                    aria-label={availableReaction.label}
                    variants={{
                      initial: {
                        scale: 0,
                      },
                      animate: {
                        scale: 1,
                        transition: {
                          duration: 0.1,
                        },
                      },
                    }}
                    aria-selected={reaction === availableReaction.value}
                    onClick={() => handleClickReaction(availableReaction.value)}
                    className={twResolve(
                      "duration-standard size-control-md shrink-0 rounded-full transition-all focus-visible:outline-none hocus:scale-110 hocus:bg-background-secondary",
                    )}
                  >
                    <img src={availableReaction.img} alt={availableReaction.label} />
                  </motion.button>
                </li>
              ))}
            </motion.ul>
          </ArkPopover.Content>
        </ArkPopover.Positioner>
      </ArkPopover.Root>
    </div>
  );
}
